import React, { useRef, useState } from "react";
import styles from "./ImageMagnifier.module.scss";
import classNames from "classnames";
import ReactDOM from "react-dom";
import { ZoomTooltip } from "../ZoomTooltip/ZoomTooltip";
import { dispatchGalleryMonetateQEvent } from "../Analytics/dispatchGalleryMonetateQEvent";

interface ImageMagnifierProps {
  src: string;
  size: 320 | 640 | 1080;
  onClick: (event: any, src: string) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  alt?: string;
  taggstar?: boolean;
  zoomTooltipPortalContainer: HTMLDivElement;
  gridIndex: number;
  showIconOnly: boolean;
  isCurrentIndex?: boolean;
  zoomOnHover?: boolean;
  taggstarDataTag?: boolean;
}

const ImageMagnifier = ({
  src,
  size,
  alt,
  taggstar = false,
  onClick,
  zoomTooltipPortalContainer,
  gridIndex,
  showIconOnly = false,
  isCurrentIndex = true,
  zoomOnHover = false,
  taggstarDataTag,
}: ImageMagnifierProps) => {
  const [isZoomLabelActive, setZoomLabelActive] = useState(false);

  const container = useRef<HTMLImageElement>(null);
  const smallImage = useRef<HTMLImageElement>(null);
  const largeImage = useRef<HTMLImageElement>(null);

  const [zoomLabelPosition, setZoomLabelPosition] = useState({
    top: "0",
    left: "0",
  });

  const handleMouseMove = (event: React.MouseEvent<HTMLImageElement>) => {
    const {
      nativeEvent: { clientX, clientY },
    } = event;

    if (isZoomLabelActive && container.current) {
      setZoomLabelPosition({
        top: `${clientY - 22}px`,
        left: `${clientX - 22}px`,
      });
    }

    if (
      zoomOnHover &&
      isZoomLabelActive &&
      smallImage.current &&
      largeImage.current
    ) {
      const smallImageBoundingClient =
        smallImage.current.getBoundingClientRect();
      const largeImageBoundingClient =
        largeImage.current.getBoundingClientRect();

      const offsetLeft = smallImageBoundingClient.left;
      const relativeMousePositionX = clientX - offsetLeft;
      const smallImageWidth = smallImageBoundingClient.width;
      const largeImageWidth = largeImageBoundingClient.width;
      const largeImageLeft =
        (relativeMousePositionX / smallImageWidth) *
        (largeImageWidth - smallImageWidth);

      const offsetTop = smallImageBoundingClient.top;
      const relativeMousePositionY = clientY - offsetTop;
      const smallImageHeight = smallImageBoundingClient.height;
      const largeImageHeight = largeImageBoundingClient.height;
      const largeImageTop =
        (relativeMousePositionY / smallImageHeight) *
        (largeImageHeight - smallImageHeight);

      largeImage.current.style.transform = `translate(-${largeImageLeft}px, -${largeImageTop}px)`;
    }
  };

  const handleFocus = () => {
    const elem = document.getElementsByClassName("zoomableImageContainer")[
      gridIndex
    ];
    if (elem) {
      const rect = elem.getBoundingClientRect();

      setZoomLabelPosition({
        top: `${rect.y + rect.height / 2 - 30}px`,
        left: `${rect.x + rect.width / 2 - 60}px`,
      });

      setZoomLabelActive(true);
    }
  };

  const handleMouseEnter = () => {
    setZoomLabelActive(true);
  };

  const handleMouseLeave = () => {
    setZoomLabelActive(false);
  };

  const handleMouseClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    onClick ? onClick(event.nativeEvent, src) : null;
    dispatchGalleryMonetateQEvent("ZoomIcon");
  };

  const handleKeyboardClick = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.nativeEvent.code === "Enter") {
      setZoomLabelActive(false);
      onClick(event.nativeEvent, src);
    }
  };

  const buildImageUrl = (src: string, size: string) => {
    return "https:" + src + (src.includes("?") ? size : `?${size}`);
  };
  const smallImageUrl = buildImageUrl(src, `$rsp-pdp-port-${size}$`);
  const largeImageUrl = buildImageUrl(src, "$fashion-ui$");

  const imageWrapperClasses = classNames({
    [styles["image-wrapper"]]: true,
    [styles["image-wrapper--disabled"]]: !zoomOnHover,
  });
  const TaggstarDataAttr = taggstarDataTag
    ? { "data-taggstar-desktop": true }
    : {};
  return (
    <div
      className={`${styles["zoomable-image-container"]} zoomableImageContainer`}
    >
      {taggstar && (
        <div
          id="mainImage"
          data-testid="main:image"
          className={classNames("slick-list", styles.taggstarPlacement)}
          {...TaggstarDataAttr}
        />
      )}

      <div
        className={imageWrapperClasses}
        ref={container}
        tabIndex={isCurrentIndex ? 0 : -1}
        role="button"
        data-cy="image"
        onMouseOver={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onBlur={handleMouseLeave}
        onClick={handleMouseClick}
        onKeyDown={handleKeyboardClick}
        onMouseDown={(event) => event.preventDefault()}
        data-testid={`grid:gallery:image:wrapper:${gridIndex}`}
        onFocus={handleFocus}
      >
        <img
          ref={smallImage}
          src={smallImageUrl}
          width={1287}
          height={1716}
          alt={alt}
          className={styles["small-image"]}
          loading="lazy"
          role="presentation"
        />

        {zoomOnHover && isZoomLabelActive && (
          <img
            ref={largeImage}
            src={largeImageUrl}
            alt={alt}
            width={1287}
            height={1716}
            loading="lazy"
            onClick={() => setZoomLabelActive(!isZoomLabelActive)}
            className={styles["large-image"]}
          />
        )}
      </div>
      {isZoomLabelActive &&
        !zoomOnHover &&
        ReactDOM.createPortal(
          <ZoomTooltip
            zoomLabelPosition={zoomLabelPosition}
            showIconOnly={showIconOnly}
          />,
          zoomTooltipPortalContainer,
        )}
    </div>
  );
};

export default ImageMagnifier;
